import { bindInternalInputPotential, bindMaterializedResource } from '../mutableWorkflowOverlay.js';
import { bindCloneWhatToolStepInput, bindCloneWhenToolStepStaticRole, getLoopFeedbackSourceOutputAddress, makeLoopLineageKey, resolveWhenBindings, upsertLoopLineageEntry, } from './loopStepHelpers.js';
import { insertStepsIntoThread, projectChangedToolStepsToWorkflowStateDelta, projectMutableStepsToTraceSteps, withThreadStepsAtIndex, withUpdatedWorkflowStepsAndState, } from './macroStepHelpers.js';
import { buildLoopContinuationPathRelocations, nullMacroStepContext, stampAndAppendTracePoint, toTopLevelStepPath, } from './tracePointHelpers.js';
export function prepareLoopContinuation(state, context, nodeName) {
    const { step, resolvedCase } = context.stepResolution;
    const rootLoopStepKey = state.rootLoopStepKeyByCloneLoopStepKey?.[step.stepKey] ?? step.stepKey;
    const loopLineageKey = makeLoopLineageKey(context.cursorContext.currentThreadIndex, rootLoopStepKey);
    const sourceOutputAddress = getLoopFeedbackSourceOutputAddress(resolvedCase.whatToolStep, step.case.loopFeedbackBindingSpec, nodeName);
    return {
        context, rootLoopStepKey, loopLineageKey, sourceOutputAddress,
        nextLineage: upsertLoopLineageEntry(state.loopLineageEntryByKey ?? {}, loopLineageKey, context.cursorContext.currentThreadIndex, rootLoopStepKey, step.stepKind, sourceOutputAddress, resolvedCase.whenToolStep.stepKey),
    };
}
// Split around host-owned clone/resource allocation to retain validation and allocation order.
export function bindLoopContinuation(state, plan, clone, nodeName) {
    const { step, resolvedCase } = plan.context.stepResolution;
    const { nextWorkflowState, changedToolStepKeys, bindingContext } = bindCloneWhatToolStepInput(state.mutableWorkflow.workflowState, clone.case.what, plan.sourceOutputAddress, step.case.loopFeedbackBindingSpec, plan.context.cursorContext.threadSteps, [clone.case.what, clone.case.when], state.toolMap, nodeName);
    const { dynamicRoleName, staticRoleName, originalDynamicEntry, originalStaticEntry } = resolveWhenBindings(state.mutableWorkflow.workflowState, resolvedCase.whenTool, resolvedCase.whenToolStep, nodeName);
    if (step.stepKind === 'for') {
        if (!originalDynamicEntry || originalDynamicEntry.workflowStateEntryKind !== 'materializedResource') {
            throw new Error(`[${nodeName}] Expected materialized dynamic input at (${resolvedCase.whenToolStep.stepKey}, ${dynamicRoleName})`);
        }
        const projection = originalDynamicEntry.projection;
        if (typeof projection !== 'number' || !Number.isInteger(projection) || projection < 0) {
            throw new Error(`[${nodeName}] Expected non-negative integer dynamic input at (${resolvedCase.whenToolStep.stepKey}, ${dynamicRoleName})`);
        }
        return {
            workflowState: nextWorkflowState, changedToolStepKeys, dynamicRoleName,
            staticRoleName, staticEntry: originalStaticEntry,
            iteration: { projection: projection + 1, resourceTypeHandle: originalDynamicEntry.resourceTypeHandle },
        };
    }
    const cloneSource = getLoopFeedbackSourceOutputAddress(clone.case.what, step.case.loopFeedbackBindingSpec, nodeName);
    const workflowState = bindInternalInputPotential(nextWorkflowState, { stepKey: clone.case.when.stepKey, roleName: dynamicRoleName }, cloneSource, bindingContext);
    changedToolStepKeys.add(clone.case.when.stepKey);
    return {
        workflowState, changedToolStepKeys, dynamicRoleName,
        staticRoleName, staticEntry: originalStaticEntry, iteration: null,
    };
}
export function applyLoopContinuation(state, plan, clone, bindings, iterationResource) {
    const { currentThreadIndex, currentStepCounter, threadSteps } = plan.context.cursorContext;
    const { step } = plan.context.stepResolution;
    let nextWorkflowState = bindings.workflowState;
    const changedToolStepKeys = new Set(bindings.changedToolStepKeys);
    if (bindings.iteration) {
        if (!iterationResource)
            throw new Error('For continuation requires a materialized iteration resource');
        nextWorkflowState = bindMaterializedResource(nextWorkflowState, { stepKey: clone.case.when.stepKey, roleName: bindings.dynamicRoleName }, iterationResource);
        changedToolStepKeys.add(clone.case.when.stepKey);
    }
    const retained = bindCloneWhenToolStepStaticRole(nextWorkflowState, clone.case.when, bindings.staticRoleName, bindings.staticEntry);
    nextWorkflowState = retained.nextWorkflowState;
    changedToolStepKeys.add(retained.changedToolStepKey);
    const insertedSteps = [step.case.what, step.case.when, clone];
    const updatedWorkflow = withUpdatedWorkflowStepsAndState(state.mutableWorkflow, withThreadStepsAtIndex(state.mutableWorkflow.stepsByThreadIndex, currentThreadIndex, insertStepsIntoThread(threadSteps, currentStepCounter, insertedSteps)), nextWorkflowState);
    const event = {
        tracePointKind: 'tick',
        workflowHandle: state.mutableWorkflow.handle,
        counterSnapshot: {
            stepCounterAfter: currentStepCounter + 1,
            ...(bindings.iteration ? { iterationCounterAfter: bindings.iteration.projection } : {}),
        },
        threadIndex: currentThreadIndex,
        stepPath: toTopLevelStepPath(currentThreadIndex, currentStepCounter),
        stepKind: step.stepKind,
        tickKind: 'loop_continuation',
        macroStepContext: nullMacroStepContext(),
        delta: {
            stepsMutation: {
                insertAt: currentStepCounter + 1,
                insertedSteps: projectMutableStepsToTraceSteps(insertedSteps),
            },
            pathRelocations: buildLoopContinuationPathRelocations(currentThreadIndex, currentStepCounter),
            workflowStateDelta: projectChangedToolStepsToWorkflowStateDelta(updatedWorkflow, changedToolStepKeys, nextWorkflowState),
        },
    };
    return {
        mutableWorkflow: updatedWorkflow,
        ...(bindings.iteration ? { iterationCounters: { [plan.loopLineageKey]: bindings.iteration.projection } } : {}),
        stepCounters: { [currentThreadIndex]: currentStepCounter + 1 },
        currentThreadIndex,
        rootLoopStepKeyByCloneLoopStepKey: {
            ...(state.rootLoopStepKeyByCloneLoopStepKey ?? {}),
            [step.stepKey]: plan.rootLoopStepKey,
            [clone.stepKey]: plan.rootLoopStepKey,
        },
        loopLineageEntryByKey: plan.nextLineage,
        ...stampAndAppendTracePoint(state, currentThreadIndex, event),
    };
}
